import { type ReactElement } from 'react'
import { messageFromError } from '@terros/common'
import { Group, Stack, Table, Text } from '@mantine/core'
import { type MaterialSymbolType, useTranslation, type usePermissionComparisons } from '@front/model'
import { MaterialSymbolIcon, NotReady } from '@components'
import { ENTITY_COLUMN_WIDTH, PERMISSION_COLUMN_WIDTH } from './permissionComparisonConstants'
import { type CompareEntity, type CompareEntityKind } from './compareUtil'

const ENTITY_ICON: Record<CompareEntityKind, MaterialSymbolType> = {
  user: 'person',
  team: 'groups',
  role: 'badge',
}

const ENTITY_LABEL: Record<CompareEntityKind, string> = {
  user: 'User',
  team: 'Team',
  role: 'Role',
}

type ComparePermissionHeaderProps = {
  entities: CompareEntity[]
  results: ReturnType<typeof usePermissionComparisons>
}

export const ComparePermissionHeader = ({ entities, results }: ComparePermissionHeaderProps): ReactElement => {
  const { t } = useTranslation()

  return (
    <Table.Thead>
      <Table.Tr>
        <Table.Th className='border-default-border bg-body sticky left-0 z-[2] border-r' w={PERMISSION_COLUMN_WIDTH}>
          {t('Permission')}
        </Table.Th>
        {entities.map((entity, index) => {
          const result = results[index]
          if (!result) return null
          return (
            <Table.Th className={index === 0 ? 'bg-brand-light' : undefined} key={entity.id} w={ENTITY_COLUMN_WIDTH}>
              <Stack gap='xs' miw={0}>
                <Group gap='xs' justify='center' wrap='nowrap'>
                  <MaterialSymbolIcon icon={ENTITY_ICON[entity.kind]} />
                  <Text fw='bold' truncate>
                    {entity.name}
                  </Text>
                </Group>
                <Text c='dimmed' size='xs' ta='center'>
                  {t(ENTITY_LABEL[entity.kind])}
                </Text>
                {(result.isLoading || result.error) && (
                  <NotReady
                    data={result.data}
                    error={messageFromError(result.error)}
                    id={`${entity.name} permissions`}
                    inline
                    isLoading={result.isLoading}
                  />
                )}
              </Stack>
            </Table.Th>
          )
        })}
      </Table.Tr>
    </Table.Thead>
  )
}
